// Utilities and shared data
import { InMemory } from '../backends/memory.js';
import { bindContext } from '../context.js';
import { Errno, ErrnoError } from '../error.js';
import { normalizePath } from '../utils.js';
import { paths as pathCache } from './cache.js';
import { size_max } from './constants.js';
import { join, resolve } from './path.js';
// descriptors
/**
 * @internal @hidden
 */
export const fdMap = new Map();
let nextFd = 100;
/**
 * @internal @hidden
 */
export function file2fd(file) {
    const fd = nextFd++;
    fdMap.set(fd, file);
    return fd;
}
/**
 * @internal @hidden
 */
export function fd2file(fd) {
    if (!fdMap.has(fd)) {
        throw new ErrnoError(Errno.EBADF);
    }
    return fdMap.get(fd);
}
/**
 * The map of mount points
 * @internal
 */
export const mounts = new Map();
// Set a default root.
mount('/', InMemory.create({ name: 'root' }));
/**
 * Mounts the file system at `mountPoint`.
 * @internal
 */
export function mount(mountPoint, fs) {
    if (mountPoint[0] !== '/') {
        mountPoint = '/' + mountPoint;
    }
    mountPoint = resolve(mountPoint);
    if (mounts.has(mountPoint)) {
        throw new ErrnoError(Errno.EINVAL, 'Mount point ' + mountPoint + ' is already in use.');
    }
    mounts.set(mountPoint, fs);
    pathCache.clear();
}
/**
 * Unmounts the file system at `mountPoint`.
 */
export function umount(mountPoint) {
    if (mountPoint[0] !== '/') {
        mountPoint = '/' + mountPoint;
    }
    mountPoint = resolve(mountPoint);
    if (!mounts.has(mountPoint)) {
        throw new ErrnoError(Errno.EINVAL, 'Mount point ' + mountPoint + ' is already unmounted.');
    }
    mounts.delete(mountPoint);
    pathCache.clear();
}
/**
 * Gets the internal `FileSystem` for the path, then returns it along with the path relative to the FS' root
 * @internal @hidden
 */
export function resolveMount(path, ctx) {
    const root = ctx?.root || '/';
    path = normalizePath(join(root, path));
    const sortedMounts = [...mounts].sort((a, b) => (a[0].length > b[0].length ? -1 : 1)); // descending order of the string length
    for (const [mountPoint, fs] of sortedMounts) {
        // We know path is normalized, so it would be a substring of the mount point.
        if (_isParentOf(mountPoint, path)) {
            path = path.slice(mountPoint.length > 1 ? mountPoint.length : 0); // Resolve the path relative to the mount point
            if (path === '') {
                path = root;
            }
            return { fs, path, mountPoint, root };
        }
    }
    throw new ErrnoError(Errno.EIO, 'No file system');
}
/**
 * Reverse maps the paths in text from the mounted FileSystem to the global path
 * @internal @hidden
 */
export function fixPaths(text, paths) {
    for (const [from, to] of Object.entries(paths)) {
        text = text?.replaceAll(from, to);
    }
    return text;
}
/**
 * Fix paths in error stacks
 * @internal @hidden
 */
export function fixError(e, paths) {
    if (typeof e.stack == 'string') {
        e.stack = fixPaths(e.stack, paths);
    }
    try {
        e.message = fixPaths(e.message, paths);
    }
    catch {
        // `message` is read only
    }
    if (e.path)
        e.path = fixPaths(e.path, paths);
    return e;
}
/**
 * @internal @deprecated
 */
export function mountObject(mounts) {
    if ('/' in mounts) {
        umount('/');
    }
    for (const [point, fs] of Object.entries(mounts)) {
        mount(point, fs);
    }
}
/**
 * @internal @hidden
 */
export function _statfs(fs, bigint) {
    const md = fs.metadata();
    const bs = md.blockSize || 4096;
    return {
        type: (bigint ? BigInt : Number)(md.type),
        bsize: (bigint ? BigInt : Number)(bs),
        ffree: (bigint ? BigInt : Number)(md.freeNodes || size_max),
        files: (bigint ? BigInt : Number)(md.totalNodes || size_max),
        bavail: (bigint ? BigInt : Number)(md.freeSpace / bs),
        bfree: (bigint ? BigInt : Number)(md.freeSpace / bs),
        blocks: (bigint ? BigInt : Number)(md.totalSpace / bs),
    };
}
export function chroot(path, inPlace) {
    const creds = this?.credentials;
    if (creds?.uid && creds?.gid && creds?.euid && creds?.egid) {
        throw new ErrnoError(Errno.EPERM, 'Can not chroot() as non-root user');
    }
    if (inPlace && this) {
        this.root += path;
        return this;
    }
    return bindContext(join(this?.root || '/', path), creds);
}
/**
 * @internal @hidden
 */
function _isParentOf(parent, child) {
    if (parent === '/' || parent === child)
        return true;
    if (!parent.endsWith('/'))
        parent += '/';
    return child.startsWith(parent);
}
